import { getReferenceString } from '@medplum/core';
import type { ActivityDefinition, Coding, Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { APPOINTMENT_TYPES_SYSTEM, ProviderRole } from '@bariendo/lib/constants';
import { PROVIDER_ROLE_CODING_MAP } from '@bariendo/lib/codings';
import { getPrimaryProviderRole } from '@bariendo/lib/profile';
import type { PractitionerWithRole } from '../types';
import type { SchedulingPageContextValue } from '../SchedulingPageContext';

function getSchedulingProviderRoleCode(role?: PractitionerRole): ProviderRole | undefined {
  const primary = getPrimaryProviderRole(role);
  if (primary) {
    return primary;
  }

  const isAdministrator =
    role?.code?.some((codeableConcept) =>
      (codeableConcept.coding ?? []).some((coding) => coding.code === ProviderRole.ADMINISTRATOR)
    ) ?? false;

  return isAdministrator ? ProviderRole.ADMINISTRATOR : undefined;
}

export function mapPractitionersToWithRole(
  practitioners: Practitioner[],
  practitionerRoles: PractitionerRole[]
): PractitionerWithRole[] {
  if (!practitioners.length) return [];

  return practitioners.map((p) => {
    const role = practitionerRoles.find(
      (r) => r.practitioner?.reference && r.practitioner.reference === getReferenceString(p)
    );

    const roleCode = getSchedulingProviderRoleCode(role);
    // Label from the code, never from the display string stored on the
    // resource: a PractitionerRole provisioned before a rename still carries
    // the old name (BAR-2571), and the code is the identity.
    const roleDisplay = roleCode ? PROVIDER_ROLE_CODING_MAP[roleCode]?.display : undefined;

    return {
      ...p,
      roleLabel: roleDisplay,
      roleCode,
    } as PractitionerWithRole;
  });
}

export function buildAppointmentTypeCodings(
  activityCodeSystem: Coding[] | null,
  getActivityDefinitionForAppointmentType: (code: string | undefined) => ActivityDefinition | undefined
): Coding[] {
  if (!activityCodeSystem) return [];

  const codingsByCode: Coding[] = [];

  activityCodeSystem.forEach((activityCode) => {
    if (!activityCode.code) return;
    const activityDefinition = getActivityDefinitionForAppointmentType(activityCode.code);
    if (!activityDefinition) return;
    const codings = activityDefinition.code?.coding ?? [];

    codings.forEach((coding) => {
      if (!coding.code) {
        return;
      }

      if (coding.system && coding.system !== APPOINTMENT_TYPES_SYSTEM) {
        return;
      }

      codingsByCode.push(coding);
    });
  });

  return codingsByCode;
}

export function buildSchedulingContextValue(
  activityCodeSystem: Coding[] | null,
  getActivityDefinitionForAppointmentType: (code: string | undefined) => ActivityDefinition | undefined
): SchedulingPageContextValue {
  const appointmentTypeCodings = buildAppointmentTypeCodings(
    activityCodeSystem,
    getActivityDefinitionForAppointmentType
  );

  return {
    appointmentTypeCodings,
    getActivityDefinitionForAppointmentType,
  };
}
